import { gql, useQuery } from '@apollo/client';
import { Alert, Button, Link, Spinner } from '@apollo/orbit';
import { Observatory } from '@apollo/space-kit/illustrations/Observatory';
import classnames from 'classnames';
import React, { useEffect } from 'react';
import { Link as ReactRouterLink, useRouteMatch } from 'react-router-dom';

import { fieldRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  clientFilterDefaults,
  useFieldInsightsFilter,
  useOperationInsightsFilter,
} from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isErrorsSort, isLatencySort } from 'src/lib/metrics';
import { DocsRouter } from 'src/lib/routers';

enum WarningMessageType {
  None,
  NoExecutionsReported,
  NoUsageReported,
  ClientFiltersWarning,
}

const fieldExecutionsLastReportedQuery = gql`
  query FieldExecutionsLastReportedQuery(
    $graphId: ID!
    $graphVariant: String!
  ) {
    graph(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        fieldUsageLastReportedAt
        fieldExecutionsLastReportedAt
      }
    }
  }
`;

const NotFoundText = ({
  loading,
  isOperation,
  resetEnabled,
  hasFieldUsage,
}: {
  loading: boolean;
  isOperation: boolean;
  resetEnabled: boolean;
  hasFieldUsage: boolean;
}) => {
  if (loading) {
    return <></>;
  }

  if (resetEnabled) {
    return <>Try resetting your filters to see more results</>;
  }

  if (isOperation || hasFieldUsage) {
    return <>Try adjusting time range to see more results</>;
  }

  return <></>;
};

export const NoSearchResultsWarning = ({
  loading,
  warningMessageType,
  isServerless,
}: {
  loading: boolean;
  warningMessageType: WarningMessageType;
  isServerless: boolean;
}) => {
  if (loading) {
    return <Spinner size="xs" />;
  }

  switch (warningMessageType) {
    case WarningMessageType.None:
      return null;
    case WarningMessageType.NoExecutionsReported:
      return (
        <Alert status="info" className="mt-8">
          {isServerless
            ? 'Your graph automatically tracks field usage, but to get field-level tracing data you will need to turn on tracing in your subgraphs. For details,'
            : 'Your graph is reporting usage information about which fields are being requested, but it is not reporting field execution information. This may mean that you have disabled field-level instrumentation in your graph, or that your graph is federated and its subgraphs do not support federated tracing. For details,'}{' '}
          <Link isExternal to={DocsRouter.path('FederationMetrics')}>
            see the docs
          </Link>
        </Alert>
      );
    case WarningMessageType.NoUsageReported:
      return (
        <Alert status="info" className="mt-8">
          Your graph is reporting field execution information, but it is not
          reporting which fields are being requested by operations. This added
          information is provided by Apollo Router or Apollo Server/Gateway
          &gt;= v3.6. For details,{' '}
          <Link isExternal to={DocsRouter.path('FieldsUsage')}>
            see the docs
          </Link>
        </Alert>
      );
    case WarningMessageType.ClientFiltersWarning:
      return (
        <Alert status="warning" className="mt-8">
          Sorting fields by latency or error metrics in combination with client
          filtering is not currently supported.
        </Alert>
      );
    default:
      return null;
  }
};

export const InsightsNoSearchResults = ({
  entity,
  resetSearch,
  graphId,
  graphVariant,
}: {
  entity: 'Field' | 'Operation' | 'Input Field' | 'Enum Value';
  resetSearch: () => void;
  graphId: string;
  graphVariant: string;
}) => {
  const isFieldRoute = useRouteMatch(fieldRouteConfig.definition);
  const {
    routeParams,
    hasActiveFilters: fieldHasActiveFilters,
    hasActiveFiltersForSubset: fieldHasActiveFiltersForSubset,
    resetToDefaultLocation: fieldResetLocation,
  } = useFieldInsightsFilter();
  const {
    hasActiveFilters: operationHasActiveFilters,
    resetToDefaultLocation: operationResetLocation,
  } = useOperationInsightsFilter();
  const trackCustomEvent = useTrackCustomEvent();

  const [currentAccountId] = useCurrentAccountId();
  const { currentPlan } = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  let warningMessageType: WarningMessageType | undefined;

  if (entity === 'Operation') {
    // No warning message if we're looking at operations
    warningMessageType = WarningMessageType.None;
  } else if (
    (isLatencySort(routeParams.fieldsSort) ||
      isErrorsSort(routeParams.fieldsSort)) &&
    fieldHasActiveFiltersForSubset(clientFilterDefaults)
  ) {
    // Show warning when we're sorting by latency or error AND filtering by client
    warningMessageType = WarningMessageType.ClientFiltersWarning;
  }

  const { data, loading } = useQuery<
    GraphQLTypes.FieldExecutionsLastReportedQuery,
    GraphQLTypes.FieldExecutionsLastReportedQueryVariables
  >(fieldExecutionsLastReportedQuery, {
    variables: {
      graphId,
      graphVariant,
    },
    // Only load field execution and field usage data if we need to
    skip: warningMessageType !== undefined,
  });

  if (warningMessageType === undefined) {
    const fieldExecutionsLastReportedAt =
      data?.graph?.variant?.fieldExecutionsLastReportedAt;
    const fieldUsageLastReportedAt =
      data?.graph?.variant?.fieldUsageLastReportedAt;

    if (
      isLatencySort(routeParams.fieldsSort) &&
      !fieldExecutionsLastReportedAt &&
      fieldUsageLastReportedAt
    ) {
      // Show warning when we're sorting by latency AND no field executions reported
      warningMessageType = WarningMessageType.NoExecutionsReported;
    } else if (fieldExecutionsLastReportedAt && !fieldUsageLastReportedAt) {
      // Show warning when there are field executions reported but no field usage reported
      warningMessageType = WarningMessageType.NoUsageReported;
    } else {
      warningMessageType = WarningMessageType.None;
    }
  }

  const isObjectField = entity === 'Field' && isFieldRoute;
  const isInputField = entity === 'Input Field' && isFieldRoute;
  const isEnumValue = entity === 'Enum Value' && isFieldRoute;

  const resetEnabled =
    isObjectField || isInputField || isEnumValue
      ? fieldHasActiveFilters
      : operationHasActiveFilters;
  const resetLocation =
    isObjectField || isInputField || isEnumValue
      ? fieldResetLocation(fieldRouteConfig)
      : operationResetLocation(insightsRouteConfig);

  useEffect(() => {
    if (warningMessageType === WarningMessageType.ClientFiltersWarning) {
      trackCustomEvent({
        category: 'Insights L3 Fields List',
        action: 'selected_client_and_latency_or_error',
        label: routeParams.fieldsSort,
      });
    }
  }, [routeParams.fieldsSort, warningMessageType, trackCustomEvent]);

  const hasFieldUsage =
    !!data?.graph?.variant?.fieldUsageLastReportedAt ||
    !!data?.graph?.variant?.fieldExecutionsLastReportedAt;

  return (
    <div className="flex h-[85%] flex-col items-center justify-center">
      <Observatory />
      <div className="flex flex-col items-center justify-center">
        <div className="pb-1 pt-2 text-center font-semibold">
          No {format.pluralize(2, entity)} Found
        </div>
        <div
          className={classnames(
            'pb-4 text-center text-neutral',
            resetEnabled && 'max-w-72',
          )}
        >
          <NotFoundText
            loading={loading}
            isOperation={entity === 'Operation'}
            resetEnabled={resetEnabled}
            hasFieldUsage={hasFieldUsage}
          />
        </div>
        {resetEnabled && (
          <Button
            className="mt-2"
            isDisabled={!resetEnabled}
            as={ReactRouterLink}
            to={resetLocation}
            variant="secondary"
            onClick={resetSearch}
          >
            Reset Filters
          </Button>
        )}
        <NoSearchResultsWarning
          loading={loading}
          warningMessageType={warningMessageType}
          isServerless={!!currentPlan?.kind.includes('SERVERLESS')}
        />
      </div>
    </div>
  );
};
